import { call, put, takeLatest } from 'redux-saga/effects';
import {
  SETTING_EMAIL_SUBMIT_FORM,
  SETTING_EMAIL_ON_CANCEL_MANAGE_RECIPIENTS,
  SETTING_EMAIL_ON_CLICK_MANAGE_RECIPIENTS,
  SETTING_EMAIL_ON_CONFIRM_MANAGE_RECIPIENTS,
  SETTING_EMAIL_ON_CLICK_DELETE_ICON,
  SETTING_EMAIL_CANCEL_DELETE_RECIPIENT,
  SETTING_EMAIL_DELETE_RECIPIENT,
  SETTING_EMAIL_PAGE_REQUEST,
} from 'actions/setting/emailNotification/constants';
import { initialize, arrayPush, arrayRemoveAll, arrayRemove } from 'redux-form';
import * as apiProfile from 'api/profile';
import * as act from 'actions';
import * as actNotif from 'actions/notifications';
import * as actEmailNotif from 'actions/setting/emailNotification';
import * as apiUpdateSetting from 'api/settings';
import * as clevertapSettingsEmail from 'clevertap/settings/email';
import { sortBy } from 'lodash';

const EMAIL_NOTIFICATION_FORM_NAME = 'emailNotification';
const SETTING_ALERT_RECIPIENTS_FIELD_NAME = 'settingAlertRecipients';

/**
 * @param {object} setting from profile.business.setting
 */
function* fillForm(setting, emailNotificationSettingRestrictions) {
  // https://mokapos.atlassian.net/browse/PS-1650
  // Sort recipients by id so that master account is at the top of recipient list.
  // There is no remove button for first account of recipient list and it's disabled. Therefore master account can't be deleted.
  const sortedRecipients = sortBy(setting.setting_alert_recipients, 'id');

  const data = {
    dailySalesSummary: setting.daily_sales_summary,
    inventoryAlerts: emailNotificationSettingRestrictions
      ? getInitialSettingValueFromRules(setting, 'inventory_alerts')
      : setting.inventory_alerts,
    promoUpdateNotif: emailNotificationSettingRestrictions
      ? getInitialSettingValueFromRules(setting, 'promo_update_notif')
      : setting.promo_update_notif,
    productUpdates: setting.product_updates,
    settingAlertRecipients: sortedRecipients,
  };
  yield put(initialize('emailNotification', data));
}

function getInitialSettingValueFromRules(setting, keyName) {
  const { settings_restrictions } = setting;
  const restriction = settings_restrictions.find(s => s.key === keyName);
  if (!restriction || !restriction.force_state) return setting[keyName];

  return restriction.forced_value;
}

function includeIsAdded(alertRecipients) {
  const newAlertReceipent = [...alertRecipients];
  newAlertReceipent.forEach(alrt => {
    alrt.is_added = true;
  });
  return newAlertReceipent;
}

function* pageRequest(request) {
  const {
    payload: { emailNotificationSettingRestrictions },
  } = request;
  try {
    yield put(act.sendingRequest(true));
    yield put(act.showProgressBar());
    let newSetting;

    if (emailNotificationSettingRestrictions) {
      const settingsRes = yield call(apiUpdateSetting.getSettings);
      const settings = settingsRes.data[0];
      yield put(actEmailNotif.setBusinessSettings(settings));

      // add is_added so that form won't show the row if users
      // does not press confirm while adding new email recipient
      newSetting = { ...settings };
      newSetting.setting_alert_recipients = includeIsAdded(
        settings.setting_alert_recipients
      );
    } else {
      const profileRes = yield call(apiProfile.getProfile);
      const profile = profileRes.data;
      yield put(actEmailNotif.setProfile(profile));
      newSetting = { ...profile.business.setting };
      newSetting.setting_alert_recipients = includeIsAdded(
        profile.business.setting.setting_alert_recipients
      );
    }
    clevertapSettingsEmail.settingEmailStart(newSetting);
    yield call(fillForm, newSetting, emailNotificationSettingRestrictions);
  } catch (error) {
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.sendingRequest(false));
    yield put(act.hideProgressBar());
  }
}

function* submitForm(request) {
  const {
    payload: { data, emailNotificationSettingRestrictions },
  } = request;
  clevertapSettingsEmail.settingEmailSave({ settings: data.settings });
  try {
    yield put(act.sendingRequest(true));
    yield put(act.showProgressBar());
    const settingsRes = yield call(apiUpdateSetting.getSettings);
    if (settingsRes.data[0] === null) {
      return;
    }

    const dataSettingsRes = settingsRes.data[0];
    data.settings.rounding_settings = dataSettingsRes.rounding_settings;
    yield call(apiUpdateSetting.putUpdateSetting, data);

    let newSetting;
    // reinitialize again form to make it not 'dirty'

    if (emailNotificationSettingRestrictions) {
      const updatedSettingsRes = yield call(apiUpdateSetting.getSettings);
      const updatedSettings = updatedSettingsRes.data[0];
      yield put(actEmailNotif.setBusinessSettings(updatedSettings));

      // add is_added so that form won't show the row if users
      // does not press confirm while adding new email recipient
      newSetting = { ...updatedSettings };
      newSetting.setting_alert_recipients = includeIsAdded(
        updatedSettings.setting_alert_recipients
      );
    } else {
      const profileRes = yield call(apiProfile.getProfile);
      const profile = profileRes.data;
      yield put(actEmailNotif.setProfile(profile));
      newSetting = { ...profile.business.setting };
      newSetting.setting_alert_recipients = includeIsAdded(
        profile.business.setting.setting_alert_recipients
      );
    }

    yield call(fillForm, newSetting, emailNotificationSettingRestrictions);
    yield put(actNotif.success('Setting has been successfully updated'));
  } catch (error) {
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.sendingRequest(false));
    yield put(act.hideProgressBar());
  }
}

export function* onCancelManageRecipients(request) {
  const {
    payload: { previousEmailNotificationForm },
  } = request;
  const formName = EMAIL_NOTIFICATION_FORM_NAME;
  const fieldName = SETTING_ALERT_RECIPIENTS_FIELD_NAME;

  // set to previous state
  yield put(arrayRemoveAll(formName, fieldName));
  for (let i = 0; i < previousEmailNotificationForm[fieldName].length; i++) {
    const d = previousEmailNotificationForm[fieldName][i];
    yield put(
      arrayPush(
        EMAIL_NOTIFICATION_FORM_NAME,
        SETTING_ALERT_RECIPIENTS_FIELD_NAME,
        d
      )
    );
  }
  yield put(actEmailNotif.hideRecipientModal());
  yield put(actEmailNotif.hideSettingAlertRecipientsErrors());
}

export function* onClickManageRecipients(request) {
  const {
    payload: { previousEmailNotificationForm },
  } = request;
  const formName = EMAIL_NOTIFICATION_FORM_NAME;
  const fieldName = SETTING_ALERT_RECIPIENTS_FIELD_NAME;

  yield put(
    actEmailNotif.setPreviousEmailNotificationForm(
      previousEmailNotificationForm
    )
  );
  yield put(arrayPush(formName, fieldName, { email: '', is_added: false }));
  yield put(actEmailNotif.showRecipientModal());
  yield put(actEmailNotif.hideSettingAlertRecipientsErrors());
}

export function* onConfirmManageRecipients(request) {
  const {
    payload: { emailNotificationForm, previousEmailNotificationForm },
  } = request;

  const formName = EMAIL_NOTIFICATION_FORM_NAME;
  const fieldName = SETTING_ALERT_RECIPIENTS_FIELD_NAME;

  // remove all blank email
  yield put(arrayRemoveAll(formName, fieldName));
  for (let i = 0; i < emailNotificationForm[fieldName].length; i++) {
    const d = emailNotificationForm[fieldName][i];
    if (d.email) {
      if (i < previousEmailNotificationForm.length) {
        yield put(arrayPush(formName, fieldName, d));
      } else {
        for (
          let j = previousEmailNotificationForm.length;
          j < emailNotificationForm[fieldName].length;
          j++
        ) {
          const index = previousEmailNotificationForm.findIndex(
            x => x.email === d.email
          );
          if (index < 0 && d.email) {
            yield put(arrayPush(formName, fieldName, { ...d, is_added: true }));
            break;
          } else {
            const em = emailNotificationForm[fieldName][j];
            const idx = previousEmailNotificationForm.findIndex(
              x => x.email === em.email
            );
            if (idx >= 0) {
              yield put(arrayRemove(formName, fieldName, j));
            }
          }
        }
      }
    }
  }

  yield put(actEmailNotif.hideRecipientModal());
}

export function* onClickDeleteIcon(request) {
  const {
    payload: { deletedRecipient },
  } = request;
  yield put(actEmailNotif.setDeletedRecipient(deletedRecipient));
  yield put(actEmailNotif.showDeleteRecipientModal());
}

export function* cancelDeleteRecipient() {
  yield put(actEmailNotif.setDeletedRecipient(null));
  yield put(actEmailNotif.hideDeleteRecipientModal());
}

export function* deleteRecipient(request) {
  const {
    payload: { deletedRecipient, emailNotificationForm },
  } = request;
  const { settingAlertRecipients } = emailNotificationForm;

  const formName = EMAIL_NOTIFICATION_FORM_NAME;
  const fieldName = SETTING_ALERT_RECIPIENTS_FIELD_NAME;
  const idx = settingAlertRecipients.indexOf(deletedRecipient);
  yield put(arrayRemove(formName, fieldName, idx));
  yield put(actEmailNotif.hideDeleteRecipientModal());
}

export default function* emailNotificationSaga() {
  yield takeLatest(SETTING_EMAIL_PAGE_REQUEST, pageRequest);
  yield takeLatest(SETTING_EMAIL_SUBMIT_FORM, submitForm);
  yield takeLatest(
    SETTING_EMAIL_ON_CANCEL_MANAGE_RECIPIENTS,
    onCancelManageRecipients
  );
  yield takeLatest(
    SETTING_EMAIL_ON_CLICK_MANAGE_RECIPIENTS,
    onClickManageRecipients
  );
  yield takeLatest(
    SETTING_EMAIL_ON_CONFIRM_MANAGE_RECIPIENTS,
    onConfirmManageRecipients
  );
  yield takeLatest(SETTING_EMAIL_ON_CLICK_DELETE_ICON, onClickDeleteIcon);
  yield takeLatest(
    SETTING_EMAIL_CANCEL_DELETE_RECIPIENT,
    cancelDeleteRecipient
  );
  yield takeLatest(SETTING_EMAIL_DELETE_RECIPIENT, deleteRecipient);
}
